import React, { useState } from 'react'
import { Input, Table, DatePicker } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useAuditLogQuery } from '../../hooks/useAuditLogQuery'
import type { AuditLogEntry } from '../../api/access-control.client'
import styles from './AuditLog.module.scss'
import shared from '../../components/AccessControl.styles.module.scss'

const { RangePicker } = DatePicker

const PAGE_SIZE = 20

const columns: ColumnsType<AuditLogEntry> = [
  {
    title: 'Actor',
    key: 'actor',
    width: 200,
    render: (_, row) => (
      <div className={styles.actorCell}>
        <span className={styles.actorName}>{row.actorName || row.actorId}</span>
      </div>
    ),
  },
  {
    title: 'Action',
    key: 'action',
    width: 280,
    render: (_, row) => <span className={styles.action}>{row.action}</span>,
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    render: (v: string) => <span className={styles.description}>{v}</span>,
  },
  {
    title: 'Timestamp',
    key: 'timestamp',
    width: 160,
    render: (_, row) => (
      <span className={styles.timestamp}>
        {dayjs(row.timestamp).format('MMM D, YYYY HH:mm')}
      </span>
    ),
  },
]

interface Props {
  targetId?: string
}

export default function AuditLog({ targetId }: Props) {
  const [search, setSearch] = useState('')
  const [dateRange, setDateRange] = useState<[string, string] | null>(null)
  const [page, setPage] = useState(1)

  const params = {
    search: search || undefined,
    dateFrom: dateRange?.[0],
    dateTo: dateRange?.[1],
    page,
    limit: PAGE_SIZE,
    targetId,
  }

  const { data, isLoading } = useAuditLogQuery(params)

  return (
    <div>
      <div className={styles.header}>
        <h3 className={styles.title}>Audit history</h3>
        <p className={styles.subtitle}>Every change made to this role.</p>
      </div>
      <div className={styles.filters}>
        <Input
          placeholder="Search actor, action, description…"
          prefix={<SearchOutlined />}
          className={`${shared.searchInput} ${styles.search}`}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          allowClear
        />
        <RangePicker
          onChange={(_, strings) => {
            setDateRange(
              strings[0] && strings[1] ? [strings[0], strings[1]] : null
            )
            setPage(1)
          }}
        />
      </div>
      <div className={shared.tableCard}>
        <Table<AuditLogEntry>
          columns={columns}
          dataSource={data?.data ?? []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total: data?.total ?? 0,
            onChange: setPage,
            showSizeChanger: false,
          }}
          size="small"
        />
      </div>
    </div>
  )
}
